// client/src/components/analytics/InstagramInsightsPanel.jsx
// Gated Instagram analytics: account insights (reach/impressions/demographics/
// best time to post), competitor benchmarking and hashtag tracking.
// Every section falls back to a "Pending Meta approval" note when the tenant's
// token doesn't yet carry instagram_manage_insights.

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppFooter from '../AppFooter';
import { FaArrowLeft, FaHome, FaInstagram } from 'react-icons/fa';
import { getMetaCapabilities } from '../../services/metaFeatures';

const baseUrl = process.env.REACT_APP_BASE_URL || '';
const j = (url, opts) => fetch(`${baseUrl}${url}`, { credentials: 'include', ...opts }).then(r => r.json());

const hour12 = (h) => {
  const n = Number(h) % 24;
  if (n === 0) return '12 AM';
  if (n === 12) return '12 PM';
  return n < 12 ? `${n} AM` : `${n - 12} PM`;
};

const PendingNote = () => (
  <p style={{ color: '#b8860b', fontSize: 13 }}>
    ⏳ Pending Meta approval — this unlocks once <code>instagram_manage_insights</code> is
    approved for your connected account.
  </p>
);

export default function InstagramInsightsPanel() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const tenantId = Number(tenant);

  const [caps, setCaps] = useState(null);
  const [insights, setInsights] = useState(null);
  const [competitors, setCompetitors] = useState([]);
  const [hashtags, setHashtags] = useState([]);
  const [newCompetitor, setNewCompetitor] = useState('');
  const [newHashtag, setNewHashtag] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    if (!tenantId) return;
    const [c, ins, comp, tags] = await Promise.allSettled([
      getMetaCapabilities(tenantId),
      j(`/api/integrations/instagram/insights?tenantId=${tenantId}&days=30`),
      j(`/api/integrations/instagram/competitors?tenantId=${tenantId}`),
      j(`/api/integrations/instagram/hashtags?tenantId=${tenantId}`),
    ]);
    if (c.status === 'fulfilled') setCaps(c.value);
    if (ins.status === 'fulfilled' && ins.value?.success) setInsights(ins.value);
    if (comp.status === 'fulfilled' && comp.value?.success) setCompetitors(comp.value.competitors || []);
    if (tags.status === 'fulfilled' && tags.value?.success) setHashtags(tags.value.hashtags || []);
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const addCompetitor = async () => {
    const u = newCompetitor.trim().replace(/^@/, '');
    if (!u) return;
    const res = await j(`/api/integrations/instagram/competitors`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, username: u }),
    });
    if (res.error === 'scope_pending_review') setMsg('Competitor tracking is pending Meta approval.');
    else { setNewCompetitor(''); load(); }
  };
  const removeCompetitor = async (id) => {
    await j(`/api/integrations/instagram/competitors/${id}?tenantId=${tenantId}`, { method: 'DELETE' });
    load();
  };
  const addHashtag = async () => {
    const h = newHashtag.trim().replace(/^#/, '');
    if (!h) return;
    const res = await j(`/api/integrations/instagram/hashtags`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, hashtag: h }),
    });
    if (res.error === 'scope_pending_review') setMsg('Hashtag tracking is pending Meta approval.');
    else { setNewHashtag(''); load(); }
  };
  const removeHashtag = async (id) => {
    await j(`/api/integrations/instagram/hashtags/${id}?tenantId=${tenantId}`, { method: 'DELETE' });
    load();
  };

  if (!tenantId) return <p style={{ color: '#ff6b6b', padding: 16 }}>Invalid tenant</p>;

  const gated = caps && caps.accountInsights === false;
  const card = { background: 'var(--surface, #fff)', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 };

  return (
    <div style={{ padding: 20, paddingBottom: 80, maxWidth: 760, margin: '0 auto' }}>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <FaInstagram style={{ color: '#C13584' }} /> Instagram Insights
      </h2>
      {msg && <div className="alert alert-info py-2">{msg}</div>}

      {/* Best time to post */}
      <div style={card}>
        <h5>Best time to post</h5>
        {gated && <PendingNote />}
        {!gated && insights?.bestTimes?.length ? (
          <p style={{ fontSize: 20, margin: 0 }}>
            {insights.bestTimes.map(hour12).join(' · ')}
            <span style={{ fontSize: 13, color: '#777' }}> (your audience's most active hours)</span>
          </p>
        ) : (!gated && <p className="text-muted">Not enough data yet — check back after a day of collection.</p>)}
      </div>

      {/* Reach / impressions */}
      <div style={card}>
        <h5>Reach &amp; impressions (last 30 days)</h5>
        {gated && <PendingNote />}
        {!gated && insights?.trend && Object.keys(insights.trend).length ? (
          <ul style={{ marginBottom: 0 }}>
            {Object.entries(insights.trend).map(([metric, series]) => {
              const total = series.reduce((s, p) => s + p.value, 0);
              return <li key={metric}><strong>{metric}</strong>: {total.toLocaleString()} total ({series.length} days)</li>;
            })}
          </ul>
        ) : (!gated && <p className="text-muted">No insight data collected yet.</p>)}
      </div>

      {/* Audience demographics */}
      <div style={card}>
        <h5>Audience demographics</h5>
        {gated && <PendingNote />}
        {!gated && insights?.demographics && Object.keys(insights.demographics).length ? (
          Object.entries(insights.demographics).map(([dim, rows]) => (
            <div key={dim} style={{ marginBottom: 8 }}>
              <strong style={{ textTransform: 'capitalize' }}>{dim}</strong>
              <ul style={{ marginBottom: 0 }}>
                {rows.slice(0, 6).sort((a, b) => b.value - a.value).map(r => (
                  <li key={r.bucket}>{r.bucket}: {r.value.toLocaleString()}</li>
                ))}
              </ul>
            </div>
          ))
        ) : (!gated && <p className="text-muted">No demographic data collected yet.</p>)}
      </div>

      {/* Competitors */}
      <div style={card}>
        <h5>Competitors</h5>
        {caps && caps.businessDiscovery === false && <PendingNote />}
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input className="form-control form-control-sm" placeholder="@competitor"
            value={newCompetitor} onChange={e => setNewCompetitor(e.target.value)} />
          <button className="btn btn-sm btn-primary" onClick={addCompetitor}>Add</button>
        </div>
        {competitors.length === 0 && <p className="text-muted">No competitors tracked.</p>}
        {competitors.length > 0 && (
          <table className="table table-sm">
            <thead><tr><th>Account</th><th>Followers</th><th>Posts</th><th>Avg. engagement</th><th /></tr></thead>
            <tbody>
              {competitors.map(c => (
                <tr key={c.id}>
                  <td>@{c.username}</td>
                  <td>{c.followers_count != null ? Number(c.followers_count).toLocaleString() : '—'}</td>
                  <td>{c.media_count != null ? Number(c.media_count).toLocaleString() : '—'}</td>
                  <td>{c.avg_engagement != null ? Number(c.avg_engagement).toLocaleString() : '—'}</td>
                  <td><button className="btn btn-sm btn-link text-danger p-0" onClick={() => removeCompetitor(c.id)}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Hashtags */}
      <div style={card}>
        <h5>Tracked hashtags</h5>
        {caps && caps.hashtagSearch === false && <PendingNote />}
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input className="form-control form-control-sm" placeholder="#hashtag"
            value={newHashtag} onChange={e => setNewHashtag(e.target.value)} />
          <button className="btn btn-sm btn-primary" onClick={addHashtag}>Track</button>
        </div>
        {hashtags.length === 0 && <p className="text-muted">No hashtags tracked.</p>}
        <ul style={{ marginBottom: 0 }}>
          {hashtags.map(h => (
            <li key={h.id}>
              #{h.hashtag}
              {h.media_count ? ` — ${Number(h.media_count).toLocaleString()} posts` : ''}
              <button className="btn btn-sm btn-link text-danger p-0 ms-2" onClick={() => removeHashtag(h.id)}>remove</button>
            </li>
          ))}
        </ul>
      </div>

      <AppFooter
        buttons={[
          { icon: <FaArrowLeft />, label: 'Back', onClick: () => navigate(-1) },
          { icon: <FaHome />, label: 'Home', onClick: () => navigate(`/${tenant}/dashboard`) },
        ]}
      />
    </div>
  );
}
